'use client'

import React from 'react'
import { Box, Stack } from '@mantine/core'
import type { Sku, SkuPriceOptionsResponse } from '@/types/interfaces/sku'
import type { SkuComponentConfig, SkuRefreshPageConfig } from '@/types/enums/sku-component-types'
import WidgetContentAccordion from '@/components/Refresh/CategoryPages/WidgetContentAccordion'
import WidgetFAQ from '@/components/Refresh/CategoryPages/WidgetFAQ'
import WidgetGenAi from '@/components/Refresh/CategoryPages/WidgetGenAi'
import WidgetTestimonialUGC from '@/components/Refresh/CategoryPages/WidgetTestimonialUGC'
import WidgetGetStartedCta from '@/components/Refresh/CategoryPages/WidgetGetStartedCta'
import { SkuPageTrustpilotReviews } from '@/components/shared/trustpilot'

/** Full-bleed widgets undo the Stack's horizontal padding with negative margins */
const fullBleedMx = { base: -16, md: 0 }

import { SkuRelatedProducts, SkuRelatedProductsByCategory } from '@/components/SkuRelatedProducts'
import { GeneralMedicineFooter } from '@/components/Footer'
import { SkuRefreshHeader } from '@/components/Refresh/SkuPages/SkuHeader/SkuRefreshHeader'
import { SkuClinicianWidget } from '@/components/Refresh/SkuPages/SkuClinicianWidget/SkuClinicianWidget'
import { ContentWAccordion } from '@/components/Refresh/SkuPages/ContentWAccordion/ContentWAccordion'
import { SkuSupportWidget } from '@/components/Refresh/SkuPages/SupportWidget/SkuSupportWidget'
import { WhatIsSkuWidget } from '@/components/Refresh/SkuPages/WhatIsSkuWidget/WhatIsSkuWidget'
import { SkuBlogRoll } from '@/components/Refresh/SkuPages/SkuBlogRoll'
import { SkuStats } from '@/components/Refresh/SkuPages/SkuStats/SkuStats'
import { InsightsWidget } from '@/components/Refresh/SkuPages/InsightsWidget/InsightsWidget'
import { buildLabAccordionConfig } from '@/components/Refresh/SkuPages/ContentWAccordion/buildLabAccordionConfig'
import DwellFrame from '@/components/Analytics/DwellFrame'

export type VisitOptionsData = {
    response: SkuPriceOptionsResponse | null
    userHomeTimezone: string | null
}

function renderSkuRefreshComponent(
    sku: Sku,
    component: SkuComponentConfig,
    index: number,
    visitOptionsData: VisitOptionsData | null,
    inPersonAvailable: boolean,
    skuStickyCtaEnabled: boolean,
) {
    switch (component.type) {
        case 'widgetContentAccordion':
            return (
                <Box key={`${component.type}-${index}`} mx={fullBleedMx}>
                    <WidgetContentAccordion
                        categoryName={component.categoryName ?? sku.category}
                        data={component.data}
                        primarySku={sku}
                    />
                </Box>
            )
        case 'widgetFaq':
            return (
                <WidgetFAQ
                    key={`${component.type}-${index}`}
                    categoryName={component.categoryName ?? sku.category}
                    data={component.data}
                />
            )
        case 'widgetGenAi':
            return (
                <WidgetGenAi
                    key={`${component.type}-${index}`}
                    categoryName={component.categoryName ?? sku.category}
                    data={component.data}
                    primarySku={sku}
                    skuPageVariant="refresh"
                />
            )
        case 'widgetTestimonialUgc':
            return (
                <WidgetTestimonialUGC
                    key={`${component.type}-${index}`}
                    categoryName={component.categoryName ?? sku.category}
                    data={component.data}
                />
            )
        case 'skuTrustpilotReviews':
            return (
                <SkuPageTrustpilotReviews key={`${component.type}-${index}`} variant={component.variant} />
            )
        case 'widgetGetStartedCta':
            return (
                <WidgetGetStartedCta
                    key={`${component.type}-${index}`}
                    categoryName={component.categoryName ?? sku.category}
                    data={component.data}
                    primarySku={sku}
                    skuPageVariant="refresh"
                    skuPage={true}
                />
            )
        case 'skuRefreshHeader':
            return (
                <SkuRefreshHeader
                    key={`${component.type}-${index}`}
                    sku={sku}
                    config={component}
                    visitOptionsData={visitOptionsData}
                    inPersonAvailable={inPersonAvailable}
                    hideMobileInlineCta={skuStickyCtaEnabled}
                />
            )
        case 'skuClinicianWidget':
            return <SkuClinicianWidget key={`${component.type}-${index}`} config={component} />
        case 'skuContentAccordion': {
            const accordionConfig =
                component.items?.length > 0
                    ? component
                    : sku.lab_details?.test_details?.length > 0
                      ? buildLabAccordionConfig(sku.lab_details, sku.lab_details.test_details)
                      : component
            return <ContentWAccordion key={`${component.type}-${index}`} sku={sku} config={accordionConfig} />
        }
        case 'skuLabDetailsAccordion': {
            if (!sku.lab_details?.test_details?.length) return null
            const accordionConfig = buildLabAccordionConfig(sku.lab_details, sku.lab_details.test_details)
            return <ContentWAccordion key={`${component.type}-${index}`} sku={sku} config={accordionConfig} />
        }
        case 'skuSupportWidget':
            return <SkuSupportWidget key={`${component.type}-${index}`} config={component} />
        case 'skuStats':
            return <SkuStats key={`${component.type}-${index}`} config={component} />
        case 'skuVideoData':
            return <WhatIsSkuWidget key={`${component.type}-${index}`} config={component} />
        case 'skuBlogRoll':
            return <SkuBlogRoll key={`${component.type}-${index}`} config={component} />
        case 'insightsWidget':
            return <InsightsWidget key={`${component.type}-${index}`} config={component} />
        case 'relatedProducts':
            if (component.category) {
                return (
                    <SkuRelatedProductsByCategory
                        key={`${component.type}-${index}`}
                        skuId={sku.id}
                        skuName={sku.name}
                        category={component.category}
                    />
                )
            }
            if (sku.related_services) {
                return (
                    <SkuRelatedProducts
                        key={`${component.type}-${index}`}
                        skuId={sku.id}
                        skuName={sku.name}
                        authenticated={sku.authenticated}
                        relatedServicesData={sku.related_services}
                    />
                )
            }
            return (
                <SkuRelatedProductsByCategory
                    key={`${component.type}-${index}`}
                    skuId={sku.id}
                    skuName={sku.name}
                    category="base"
                />
            )
        default: {
            const _exhaustive: never = component
            void _exhaustive
            return null
        }
    }
}

export default function SkuRefreshRenderer({
    sku,
    config,
    visitOptionsData,
    inPersonAvailable,
    skuStickyCtaEnabled = false,
}: {
    sku: Sku
    config: SkuRefreshPageConfig
    visitOptionsData: VisitOptionsData | null
    inPersonAvailable: boolean
    skuStickyCtaEnabled?: boolean
}) {
    return (
        <Stack gap="md" mx={{ base: 16, sm: 24 }}>
            {config.components.map((c, i) => (
                <DwellFrame key={`${c.type}-${i}`} frameId={`refresh:${c.type}`} frameIndex={i}>
                    {renderSkuRefreshComponent(sku, c, i, visitOptionsData, inPersonAvailable, skuStickyCtaEnabled)}
                </DwellFrame>
            ))}
            <GeneralMedicineFooter />
        </Stack>
    )
}
